import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import DateInput from "./DateInput";

type Dict = Record<string, string>;

// Ported from static/js/app.js's SilverHV object.
const WORST_ORD: Record<string, number> = { missed: 0, failed: 1, missed_sla: 2, delayed: 3, inprog: 4, in_progress: 4, success: 5, scheduled: 6 };
const STATUS_CLS: Dict = {
  success: "hg-cs-success", inprog: "hg-cs-inprog", in_progress: "hg-cs-inprog", delayed: "hg-cs-delayed",
  missed_sla: "hg-cs-missed-sla", missed: "hg-cs-missed", failed: "hg-cs-failed", scheduled: "hg-cs-scheduled",
};
const DOT_COLOR: Dict = {
  success: "#1e8e3e", inprog: "#2f7bc4", in_progress: "#2f7bc4", delayed: "#f79227",
  missed_sla: "#e65100", missed: "#f54b3c", failed: "#8e44ad", scheduled: "#9e9e9e",
};
const LEGEND = [
  { status: "success", label: "On Time" },
  { status: "inprog", label: "In Progress" },
  { status: "delayed", label: "Delayed Start" },
  { status: "missed_sla", label: "Missed SLA" },
  { status: "missed", label: "Missed" },
  { status: "failed", label: "Failed" },
  { status: "scheduled", label: "Scheduled" },
];
const UNIFIED_STATUSES = ["success", "delayed", "missed_sla", "inprog", "missed", "failed", "scheduled"];
const SLA_LABEL: Dict = {
  success: "On Time", on_time: "On Time", ontime: "On Time", delayed: "Delayed Start", inprog: "In Progress",
  missed_sla: "Missed SLA", missed: "Overdue", failed: "\u2014", scheduled: "Scheduled",
};
const SLA_COLOR: Dict = {
  success: "#1e8e3e", on_time: "#1e8e3e", ontime: "#1e8e3e", delayed: "#f79227", inprog: "#2f7bc4",
  missed_sla: "#e65100", missed: "#f54b3c", failed: "#9e9e9e", scheduled: "#9e9e9e",
};
const RUN_LABEL: Dict = {
  success: "Completed", on_time: "Completed", delayed: "Completed", inprog: "In Progress", missed_sla: "Completed",
  missed: "Not Started", scheduled: "Not Started", failed: "Failed", succeeded: "Completed",
  in_progress: "In Progress", cancelled: "Cancelled", canceled: "Cancelled",
};
const RUN_COLOR: Dict = {
  success: "#1e8e3e", on_time: "#1e8e3e", delayed: "#f79227", inprog: "#2f7bc4",
  missed_sla: "#1e8e3e",  // job completed (even if SLA missed) → green like success
  missed: "#9e9e9e", scheduled: "#9e9e9e", failed: "#8e44ad", succeeded: "#1e8e3e", in_progress: "#2f7bc4",
  cancelled: "#9e9e9e", canceled: "#9e9e9e",
};

interface TreeNode {
  id: string;
  label: string;
  lvl: number;
  parent?: string;
  children?: string[];
  leaf?: string;
}

interface HourAgg {
  count: number;
  status: string;
  assets: any[];
}

function worstStatus(a: string, b: string): string {
  return (WORST_ORD[a] ?? 9) <= (WORST_ORD[b] ?? 9) ? a : b;
}
function hLabel(h: number): { a: string; b: string } {
  if (h === 0) return { a: "12 AM", b: "midnight" };
  if (h === 12) return { a: "12 PM", b: "noon" };
  return h < 12 ? { a: `${h} AM`, b: `${String(h).padStart(2, "0")}:00` } : { a: `${h - 12} PM`, b: `${String(h).padStart(2, "0")}:00` };
}

function buildTree(groups: Record<string, any>): TreeNode[] {
  const ibpChildren: string[] = [], mosaicChildren: string[] = [];
  Object.entries<any>(groups).forEach(([key, g]) => {
    if (g.application === "Mosaic") mosaicChildren.push(key);
    else ibpChildren.push(key);
  });
  ibpChildren.sort((a, b) => a.localeCompare(b));
  mosaicChildren.sort((a, b) => a.localeCompare(b));
  const nodes: TreeNode[] = [{ id: "ALL", label: "All", lvl: 0, children: [] }];
  if (ibpChildren.length) {
    nodes[0].children!.push("IBP");
    nodes.push({ id: "IBP", label: "IBP", lvl: 1, parent: "ALL", children: ibpChildren });
    ibpChildren.forEach((k) => {
      const g = groups[k];
      nodes.push({ id: k, label: g.market || k.split(".")[1] || k, lvl: 2, parent: "IBP", leaf: k });
    });
  }
  if (mosaicChildren.length) {
    nodes[0].children!.push("Mosaic");
    nodes.push({ id: "Mosaic", label: "Mosaic", lvl: 1, parent: "ALL", children: mosaicChildren });
    mosaicChildren.forEach((k) => {
      nodes.push({ id: k, label: "All assets", lvl: 2, parent: "Mosaic", leaf: k });
    });
  }
  return nodes;
}

function leafKeys(nodes: TreeNode[], node: TreeNode): string[] {
  if (node.leaf) return [node.leaf];
  return (node.children || [])
    .map((id) => nodes.find((n) => n.id === id))
    .filter(Boolean)
    .flatMap((c) => leafKeys(nodes, c as TreeNode));
}

function isVisible(nodes: TreeNode[], node: TreeNode, collapsed: Set<string>): boolean {
  if (!node.parent) return true;
  if (collapsed.has(node.parent)) return false;
  const p = nodes.find((n) => n.id === node.parent);
  return p ? isVisible(nodes, p, collapsed) : true;
}

function aggHours(nodes: TreeNode[], node: TreeNode, groups: Record<string, any>): Record<number, HourAgg> {
  const lk = leafKeys(nodes, node);
  const by: Record<number, HourAgg> = {};
  lk.forEach((k) => {
    const g = groups[k] || {};
    Object.entries<any>(g.hours || {}).forEach(([h, hv]) => {
      const hi = parseInt(h, 10);
      if (!by[hi]) by[hi] = { count: 0, status: "scheduled", assets: [] };
      by[hi].count += hv.count;
      by[hi].status = worstStatus(by[hi].status, hv.status);
      by[hi].assets.push(...(hv.assets || []));
    });
  });
  return by;
}

interface HourlyViewProps {
  date: string;
  onDateChange: (date: string) => void;
  dateMin?: string;
  dateMax?: string;
  onOpenAsset: (name: string) => void;
}

const HourlyView: React.FC<HourlyViewProps> = ({ date, onDateChange, dateMin, dateMax, onOpenAsset }) => {
  const [data, setData] = useState<any>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [mode, setMode] = useState<"count" | "status" | "both">("count");
  const [selected, setSelected] = useState<{ key: string; hour: number } | null>(null);
  const [detail, setDetail] = useState<{ title: string; assets: any[] } | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setDetail(null);
      setSelected(null);
      const { ok, data: json, error: err } = await api.silverHourly(date ? { date } : undefined);
      if (cancelled) return;
      if (!ok) {
        setError("Failed to load hourly data: " + err);
        setData(null);
        return;
      }
      setError("");
      setData(json);
      setCollapsed(new Set());
    }
    load();
    return () => { cancelled = true; };
  }, [date]);

  const groups = (data && data.groups) || {};
  const nodes = useMemo(() => buildTree(groups), [groups]);
  const nowHour = new Date().getUTCHours();

  function toggleRow(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function showDetail(leafKey: string, hour: number) {
    const g = groups[leafKey] || {};
    const entry = (g.hours || {})[String(hour)];
    if (!entry) return;
    setSelected({ key: leafKey, hour });
    const l = hLabel(hour), nl = hLabel((hour + 1) % 24);
    setDetail({ title: `${leafKey.split(".").slice(1).join(" \u203a ")} \u2014 ${l.a}\u2013${nl.a} UTC`, assets: entry.assets || [] });
  }

  function clickAgg(nodeId: string, hour: number) {
    setCollapsed((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const lk = leafKeys(nodes, node);
    const allAssets: any[] = [];
    lk.forEach((k) => {
      const g = groups[k] || {};
      const entry = (g.hours || {})[String(hour)];
      if (entry && entry.assets) {
        entry.assets.forEach((a: any) => allAssets.push({ ...a, market: a.market || g.market || k.split(".")[1] || k }));
      }
    });
    allAssets.sort((a, b) => {
      const wa = WORST_ORD[a.status] ?? 9, wb = WORST_ORD[b.status] ?? 9;
      return wa !== wb ? wa - wb : (a.name || "").localeCompare(b.name || "");
    });
    setSelected({ key: nodeId, hour });
    const l = hLabel(hour), nl = hLabel((hour + 1) % 24);
    const label = nodeId === "ALL" ? "All Groups" : node.label;
    setDetail({ title: `${label} \u2014 ${l.a}\u2013${nl.a} UTC`, assets: allAssets });
  }

  const detailCounts = useMemo(() => {
    if (!detail) return {} as Record<string, number>;
    const c: Record<string, number> = {};
    detail.assets.forEach((a: any) => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [detail]);

  return (
    <>
      <div className="hv-info-strip">
        &#8505;&nbsp; Expected times use <strong>historical avg start / end</strong> where available, falling back to CSV schedule. All times UTC.
        Click a coloured cell to see individual assets. Current hour highlighted.
      </div>
      <div className="hv-filters">
        <span className="hv-filter-label">Date</span>
        <DateInput className="hv-filter-ctrl" value={date} min={dateMin} max={dateMax} onChange={(e) => onDateChange(e.target.value)} />
        <div className="hv-filter-sep"></div>
        <span className="hv-filter-label">Cell shows</span>
        <div className="hv-mode-toggle">
          <button className={mode === "count" ? "active" : ""} onClick={() => setMode("count")}>Count</button>
          <button className={mode === "status" ? "active" : ""} onClick={() => setMode("status")}>Status dots</button>
          <button className={mode === "both" ? "active" : ""} onClick={() => setMode("both")}>Both</button>
        </div>
      </div>
      <div className="hv-legend">
        {LEGEND.map((l) => (
          <span key={l.status} className="hv-legend-item">
            <span className="hv-legend-dot" style={{ background: DOT_COLOR[l.status] }}></span>{l.label}
          </span>
        ))}
      </div>
      <div className="hv-grid-wrap">
        <table className="hgrid">
          <thead>
            <tr>
              <th className="hg-col-group">Group / Market</th>
              {Array.from({ length: 24 }, (_, i) => {
                const l = hLabel(i), isCur = i === nowHour;
                return (
                  <th key={i} className={isCur ? "hg-col-now" : ""}>
                    <span className="hg-hlbl">{l.a}</span>
                    <span className="hg-hsub">{isCur ? "\u25bc NOW" : l.b}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={25} style={{ textAlign: "center", padding: 40, color: "#64748b" }}>{error || "Loading hourly data\u2026"}</td></tr>
            ) : (
              nodes.map((node) => {
                const vis = isVisible(nodes, node, collapsed);
                const hdata = aggHours(nodes, node, groups);
                const hasChildren = !!(node.children && node.children.length);
                const isCollapsed = collapsed.has(node.id);
                const indentCls = node.lvl === 1 ? "hg-indent-1" : node.lvl === 2 ? "hg-indent-2" : "";
                return (
                  <tr key={node.id} className={`hg-lvl${node.lvl}${vis ? "" : " hg-hidden"}`}>
                    <td className="hg-gcell">
                      <div className={`hg-gcell-inner ${indentCls}`}>
                        {hasChildren ? (
                          <span className={`hg-gcell-arrow${isCollapsed ? "" : " open"}`} onClick={() => toggleRow(node.id)}>&#9654;</span>
                        ) : (
                          <span style={{ width: 18, display: "inline-block" }}></span>
                        )}
                        <span>{node.label}</span>
                      </div>
                    </td>
                    {Array.from({ length: 24 }, (_, i) => {
                      const e = hdata[i];
                      const isCur = i === nowHour;
                      if (!e) return <td key={i} className={`hg-hcell${isCur ? " hg-now-bg" : ""}`}><div className="hg-cell-inner"></div></td>;
                      const sc = STATUS_CLS[e.status] || "hg-cs-scheduled";
                      const isSel = !!(selected && selected.key === node.id && selected.hour === i);
                      return (
                        <td
                          key={i}
                          className={`hg-hcell hg-clickable ${sc}${isCur ? " hg-now-bg" : ""}${isSel ? " hg-selected" : ""}`}
                          onClick={() => (node.leaf ? showDetail(node.leaf, i) : clickAgg(node.id, i))}
                        >
                          <div className="hg-cell-inner">
                            {(mode === "count" || mode === "both") && <span className="hg-cell-count">{e.count}</span>}
                            {(mode === "status" || mode === "both") && (
                              <span className="hg-cell-dots">
                                {(e.assets || []).slice(0, 10).map((a: any, ai: number) => (
                                  <span key={ai} className="hg-cell-dot" style={{ background: DOT_COLOR[a.status] || "#ccc" }}></span>
                                ))}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="hv-detail-panel">
          <div className="hv-detail-hdr">
            <h3>{detail.title}</h3>
            <button className="hv-detail-close" onClick={() => { setDetail(null); setSelected(null); }}>&#10005;</button>
          </div>
          <div className="hv-detail-meta">
            <span>
              <strong>{detail.assets.length}</strong> expected
              {detailCounts.success ? <span style={{ color: "#1e8e3e", fontWeight: 600 }}>&nbsp;|&nbsp;&#10003; {detailCounts.success} on time</span> : null}
              {detailCounts.delayed ? <span style={{ color: "#f79227", fontWeight: 600 }}>&nbsp;|&nbsp;&#9888; {detailCounts.delayed} delayed start</span> : null}
              {detailCounts.missed_sla ? <span style={{ color: "#e65100", fontWeight: 600 }}>&nbsp;|&nbsp;&#9201; {detailCounts.missed_sla} missed SLA</span> : null}
              {detailCounts.inprog ? <span style={{ color: "#2f7bc4", fontWeight: 600 }}>&nbsp;|&nbsp;&#9654; {detailCounts.inprog} running</span> : null}
              {detailCounts.missed ? <span style={{ color: "#f54b3c", fontWeight: 600 }}>&nbsp;|&nbsp;&#8856; {detailCounts.missed} missed</span> : null}
              {detailCounts.failed ? <span style={{ color: "#8e44ad", fontWeight: 600 }}>&nbsp;|&nbsp;&#10007; {detailCounts.failed} failed</span> : null}
              {detailCounts.scheduled ? <span style={{ color: "#9e9e9e" }}>&nbsp;|&nbsp;{detailCounts.scheduled} upcoming</span> : null}
            </span>
          </div>
          <table className="hv-dtable">
            <thead>
              <tr>
                <th>Asset</th><th>Market</th><th>GDO</th><th>Exp Start (UTC)</th><th>Exp End (UTC)</th>
                <th>Act Start</th><th>Act End</th><th>Duration</th><th>SLA Status</th><th>Run Status</th>
              </tr>
            </thead>
            <tbody>
              {detail.assets.map((a: any, i: number) => {
                const statusKey = UNIFIED_STATUSES.includes(a.status) ? a.status : null;
                return (
                  <tr key={i} className="hv-det-row" onClick={() => onOpenAsset(a.name)} title="Click to open full detail sidebar">
                    <td className="hv-det-asset">
                      {a.name}
                      {a.src === "hist" ? <span className="hv-src-hist">Hist</span> : <span className="hv-src-csv">CSV</span>}
                    </td>
                    <td>{a.market || "\u2014"}</td>
                    <td>{a.gdo || "\u2014"}</td>
                    <td>{a.expStart || "\u2014"}</td>
                    <td>{a.expEnd || "\u2014"}</td>
                    <td>{a.aStart || "\u2014"}</td>
                    <td>{a.aEnd || "\u2014"}</td>
                    <td>{a.dur || "\u2014"}</td>
                    <td>{statusKey ? <span style={{ color: SLA_COLOR[statusKey] || "#64748b", fontWeight: 600 }}>{SLA_LABEL[statusKey] || statusKey}</span> : "\u2014"}</td>
                    <td>{statusKey ? <span style={{ color: RUN_COLOR[statusKey] || "#64748b", fontWeight: 600 }}>{RUN_LABEL[statusKey] || a.runStatus}</span> : "\u2014"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default HourlyView;
